import React, { forwardRef, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { ScrollText, Grid, Settings, Trophy, Info, CalendarDays, Mail, History } from 'lucide-react';
import { LEVELS, getVisibleLevels, getLevelForPoints, getProgressToNextLevel, ladderPointsFor } from '../../../config/levelsConfig';
import LevelAnimalIcon from './LevelAnimalIcon';
import LevelSprite from './LevelSprite';
import { getStudentDisplayName } from '../../../config/studentNameMapping';
import { getClassLeaderboard } from '../api/students';
import LevelCloseUp from './LevelCloseUp';
import './MobileStudentNav.css';

/**
 * Animated counter that ticks up/down to target value
 * Reports animation state via onAnimatingChange callback
 */
function AnimatedCounter({ value, duration = 500, onAnimatingChange }) {
  const [displayValue, setDisplayValue] = useState(value);
  const [isAnimating, setIsAnimating] = useState(false);
  const prevValueRef = useRef(value);

  useEffect(() => {
    const prevValue = prevValueRef.current;
    const diff = value - prevValue;

    if (diff === 0) return;

    setIsAnimating(true);
    onAnimatingChange?.(true, diff > 0); // Report animation start and direction

    const steps = Math.abs(diff);
    const stepDuration = Math.min(duration / steps, 150); // Cap at 150ms per step for slower counting
    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      const newValue = prevValue + (diff > 0 ? currentStep : -currentStep);
      setDisplayValue(newValue);

      if (currentStep >= steps) {
        clearInterval(timer);
        prevValueRef.current = value;
        setTimeout(() => {
          setIsAnimating(false);
          onAnimatingChange?.(false, false);
        }, 300);
      }
    }, stepDuration);

    return () => clearInterval(timer);
  }, [value, duration, onAnimatingChange]);

  // Key changes on each tick to retrigger animation
  return (
    <span key={displayValue} className={isAnimating ? 'counting' : ''}>
      {displayValue}
    </span>
  );
}

/**
 * Trophy icon for leaderboard access
 */
function LevelBadgeCompact() {
  return (
    <div className="level-badge-compact">
      <Trophy size={22} className="trophy-icon" />
    </div>
  );
}

/**
 * Subtle top bar for less-used actions (History, Performances, Cartoons, Settings)
 * Non-sticky, scrolls with content, very minimal
 */
/**
 * Collapsible info box explaining how coins are earned.
 * Shows a small info icon; tap to expand/collapse the explanation.
 */
function CoinInfoBox() {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`coin-info-box${expanded ? ' expanded' : ''}`}>
      <button
        className="coin-info-toggle"
        onClick={() => setExpanded(prev => !prev)}
        aria-label="积分说明"
        aria-expanded={expanded}
        type="button"
      >
        <Info size={18} />
      </button>
      {expanded && (
        <div className="coin-info-content">
          <div className="coin-info-title">积分怎么来的？</div>
          <div className="coin-info-rows">
            <span>🎧 每日听力 → +10</span>
            <span>📖 每日读写 → +10</span>
            <span>✅ 完成作业 → +10</span>
            <span>⭐ 满分通过 → 2x!</span>
            <span>🔄 返工完成 → +1/题</span>
            <span>🎵 儿歌每颗星 → +10</span>
            <span>❌ 未通过 → -1/题</span>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Inline bottom sheet showing the animal levels reached so far, plus the next one
 */
function LevelBottomSheet({ points, onClose }) {
  const [zoomedLevel, setZoomedLevel] = useState(null);
  const currentLevel = getLevelForPoints(points);
  // Which animal stands at the top: the current one, or a past one the child tapped.
  const [shownLevel, setShownLevel] = useState(currentLevel);
  const heroRef = useRef(null);
  // The child's own row: the list opens scrolled to it, not to 小书虫 at the top.
  const currentRowRef = useRef(null);
  const progress = getProgressToNextLevel(points);

  useEffect(() => {
    currentRowRef.current?.scrollIntoView?.({ block: 'center' });
  }, []);

  const zoomProps = (level) => ({
    onClick: (e) => {
      e.stopPropagation();
      setZoomedLevel(level);
    },
    role: 'button',
    tabIndex: 0,
    onKeyDown: (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setZoomedLevel(level);
      }
    },
    'aria-label': `查看 ${level.name}`,
  });

  // An unlocked level's row brings its animal back to the top (it walks in again);
  // a locked one still opens the close-up with how far away it is.
  const show = (level) => {
    setShownLevel(level);
    heroRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  };
  const showProps = (level) => ({
    onClick: (e) => { e.stopPropagation(); show(level); },
    role: 'button',
    tabIndex: 0,
    onKeyDown: (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); show(level); }
    },
    'aria-label': `查看 ${level.name}`,
  });

  return (
    <div className="level-sheet-overlay" onClick={onClose} data-testid="level-sheet-overlay">
      <div className="level-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="level-sheet-handle" />
        <CoinInfoBox />

        {/* Hero: the level animal — walks in, emotes, emotes again on every tap */}
        <div className="level-sheet-hero" ref={heroRef}>
          <LevelSprite key={shownLevel.level} level={shownLevel.level} icon={shownLevel.icon} name={shownLevel.name} />
          <div className="level-sheet-hero-info">
            <span className="level-sheet-hero-name">{shownLevel.name}</span>
            <span className="level-sheet-hero-lv">Lv.{shownLevel.level}</span>
          </div>
          <div className="level-sheet-progress-bar">
            <div
              className="level-sheet-progress-fill"
              style={{ width: `${progress.progress}%` }}
            />
          </div>
          <div className="level-sheet-progress-text">
            {progress.isMaxLevel ? (
              <span className="level-sheet-progress-total">{points} 积分 · 满级!</span>
            ) : (
              <>
                <span className="level-sheet-progress-total">
                  {points} / {progress.nextLevelPoints} 积分
                </span>
                <span className="level-sheet-progress-hint">
                  还需 {progress.pointsToNext} 积分升级
                </span>
              </>
            )}
          </div>
        </div>

        {/* List: levels reached, plus exactly one ahead */}
        <div className="level-sheet-list">
          {getVisibleLevels(points).map((lvl) => {
            const isCurrent = lvl.level === currentLevel.level;
            const isUnlocked = points >= lvl.minPoints;
            const rowVariant = lvl.variant;
            return (
              <div
                key={lvl.level}
                ref={isCurrent ? currentRowRef : undefined}
                className={`level-sheet-row zoomable${isCurrent ? ' current' : isUnlocked ? ' unlocked' : ' locked'}${rowVariant ? ` variant-${rowVariant}` : ''}${lvl.level === shownLevel.level ? ' shown' : ''}`}
                {...(isUnlocked ? showProps(lvl) : zoomProps(lvl))}
              >
                <div
                  className={`level-sheet-row-icon${rowVariant ? ` ${rowVariant}` : ''}`}
                  style={!rowVariant ? {
                    background: `linear-gradient(145deg, ${lvl.colorLight} 0%, ${lvl.color} 100%)`,
                  } : undefined}
                >
                  <span>{lvl.icon}</span>
                </div>
                <span className="level-sheet-row-name">{lvl.name}</span>
                {/* "+" marks this as the tier's entry threshold, not a target */}
                <span className="level-sheet-row-points">{lvl.minPoints}+ 积分</span>
                <span className="level-sheet-row-status">
                  {isCurrent ? '当前' : isUnlocked ? '✓' : '🔒'}
                </span>
              </div>
            );
          })}
        </div>

        {zoomedLevel && (
          <LevelCloseUp
            level={zoomedLevel}
            points={points}
            onClose={() => setZoomedLevel(null)}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Leaderboard bottom sheet — single scrollable view
 * Each class card shows rank, name, score, then a 2-column grid of students
 */
function LeaderboardSheet({ studentId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getClassLeaderboard(studentId)
      .then(result => {
        if (!cancelled) setData(result);
      })
      .catch(() => {
        if (!cancelled) setData({ myClassIds: [], classes: [] });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [studentId]);

  const myClassIds = data?.myClassIds || [];
  // Filter: minimum 2 students and 10 weekly points to appear on leaderboard
  const eligibleClasses = (data?.classes || []).filter(
    cls => (cls.students?.length || 0) >= 2 && cls.weeklyScore >= 10
  );

  return (
    <div className="level-sheet-overlay" onClick={onClose} data-testid="leaderboard-sheet-overlay">
      <div className="leaderboard-sheet level-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="level-sheet-handle" />
        <CoinInfoBox />

        <div className="leaderboard-header">
          <span className="leaderboard-title">本周班级排行</span>
        </div>

        <div className="leaderboard-list">
          {loading ? (
            <div className="leaderboard-loading">加载中...</div>
          ) : eligibleClasses.length === 0 ? (
            <div className="leaderboard-empty">暂无数据</div>
          ) : (
            eligibleClasses.map((cls, i) => {
              const rank = i + 1;
              const isMyClass = myClassIds.includes(cls.classId);
              return (
                <div
                  key={cls.classId}
                  className={`leaderboard-class-card${isMyClass ? ' my-class' : ''}`}
                  data-testid={`class-row-${cls.classId}`}
                >
                  <div className="leaderboard-class-header">
                    <span className={`leaderboard-rank${rank <= 3 ? ' gold' : ''}`}>
                      {rank <= 3 ? ['🥇', '🥈', '🥉'][rank - 1] : rank}
                    </span>
                    <span className="leaderboard-name">{cls.className}</span>
                    <span className="leaderboard-score">{cls.weeklyScore}</span>
                  </div>
                  {cls.students?.length > 0 && (
                    <div className="leaderboard-students-grid">
                      {cls.students.map(s => {
                        const level = getLevelForPoints(s.totalScore);
                        const isCurrent = s.studentId === studentId;
                        return (
                          <div
                            key={s.studentId}
                            className={`leaderboard-student-cell${isCurrent ? ' current' : ''}`}
                          >
                            <span className="leaderboard-student-icon">{level.icon}</span>
                            <span className="leaderboard-student-name">
                              {getStudentDisplayName(s.studentId, s.studentName)}
                            </span>
                            {/* <span className="leaderboard-student-score">{s.weeklyScore}</span> */}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

// beiski's top-of-the-ladder preview lives in levelsConfig, shared with kids songs (Rob, 2026-10-16)
export { ladderPointsFor };

export const MobileStudentTopBar = forwardRef(function MobileStudentTopBar(
  { studentId, displayName, totalCoins, coinRef, isTeacherView = false, onSettingsClick, onCalendarClick, onNotificationClick, onHistoryClick, unseenCount = 0 },
  ref
) {
  const navigate = useNavigate();
  const [isReceivingCoins, setIsReceivingCoins] = useState(false);
  const [showLevels, setShowLevels] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);

  const handleLevelClick = () => {
    if (!isTeacherView) {
      setShowLeaderboard((prev) => !prev);
    }
  };

  const handleAvatarClick = () => {
    if (!isTeacherView) {
      setShowLevels((prev) => !prev);
    }
  };

  const handleXpAnimatingChange = (isAnimating, isIncreasing) => {
    // Only show receiving animation when XP is increasing
    setIsReceivingCoins(isAnimating && isIncreasing);
  };

  const ladderPoints = ladderPointsFor(studentId, totalCoins);
  const level = getLevelForPoints(ladderPoints);

  return (
    <div className={`mobile-student-top-bar${isTeacherView ? ' teacher-view' : ''}`}>
      {/* Avatar with name - clickable to open levels sheet */}
      <div
        className={`top-bar-avatar${isTeacherView ? '' : ' clickable'}`}
        onClick={handleAvatarClick}
        role={isTeacherView ? undefined : 'button'}
      >
        <div
          className={`avatar-circle${level.variant ? ` ${level.variant}` : ''}`}
          style={!level.variant ? {
            background: `linear-gradient(145deg, ${level.colorLight} 0%, ${level.color} 100%)`,
            boxShadow: `0 2px 0 ${level.colorDark}, 0 3px 6px ${level.color}40`,
          } : undefined}
        >
          {/* the real pixel animal (its emoji until the sprite arrives) — Rob, 2026-10-16 */}
          <LevelAnimalIcon level={level.level} icon={level.icon} />
        </div>
        <span className="avatar-name">{displayName || '同学'}</span>
      </div>

      {/* Trophy + Leaderboard hidden for now — ship later */}
      {false && totalCoins !== undefined && (
        <div
          ref={coinRef}
          className={`top-bar-level${isTeacherView ? ' readonly' : ''}${isReceivingCoins ? ' receiving' : ''}`}
          onClick={handleLevelClick}
          role={isTeacherView ? undefined : 'button'}
        >
          <LevelBadgeCompact
            points={totalCoins}
            isReceiving={isReceivingCoins}
            onAnimatingChange={handleXpAnimatingChange}
            AnimatedCounter={AnimatedCounter}
          />
        </div>
      )}

      {/* Level bottom sheet — portal to body to escape backdrop-filter containing block */}
      {showLevels && createPortal(
        <LevelBottomSheet
          points={ladderPoints}
          onClose={() => setShowLevels(false)}
        />,
        document.body
      )}

      {/* Leaderboard bottom sheet */}
      {false && showLeaderboard && createPortal(
        <LeaderboardSheet
          studentId={studentId}
          onClose={() => setShowLeaderboard(false)}
        />,
        document.body
      )}

      {/* Right-side action buttons */}
      <div className="top-bar-actions">
        <button className="top-bar-btn" onClick={onNotificationClick} aria-label="批改反馈" type="button">
          <Mail size={22} />
          {unseenCount > 0 && <span className="top-bar-badge">{unseenCount}</span>}
        </button>
        <button className="top-bar-btn" onClick={onHistoryClick} aria-label="历史" type="button">
          <History size={22} />
        </button>
        <button className="top-bar-btn" onClick={onCalendarClick} aria-label="日历" type="button">
          <CalendarDays size={22} />
        </button>
        <button className="top-bar-btn" onClick={onSettingsClick} aria-label="设置" type="button">
          <Settings size={22} />
        </button>
      </div>
    </div>
  );
});

/**
 * Mobile bottom navigation for student dashboard
 * Shows nav buttons for Home, Map, History
 * (Performances, Cartoons, Settings are in the top bar)
 */
export const MobileStudentNav = forwardRef(function MobileStudentNav(
  { studentId, mapRef, dailyCount = 0, isTeacherView = false, activeTab: propActiveTab, onTabChange },
  ref
) {
  const navigate = useNavigate();
  const location = useLocation();

  // Determine active tab from URL path segment after studentId (for student view)
  // or use prop (for teacher view)
  const pathAfterStudent = location.pathname.split('/').pop();
  const activeTab = propActiveTab || (
    ['map', 'settings', 'performances', 'cartoons', 'levels'].includes(pathAfterStudent)
      ? pathAfterStudent
      : 'daily'
  );

  const handleNavClick = (tab) => {
    if (isTeacherView && onTabChange) {
      // Teacher view: use local state
      onTabChange(tab);
    } else {
      // Student view: navigate via URL
      const path = tab === 'daily' ? `/dashboard/${studentId}` : `/dashboard/${studentId}/${tab}`;
      navigate(path);
    }
  };

  return (
    <div className={`mobile-student-nav-container${isTeacherView ? ' teacher-view' : ''}`}>
      <nav className="mobile-student-nav">
        <button
          className={`nav-btn ${activeTab === 'daily' ? 'active' : ''}`}
          onClick={() => handleNavClick('daily')}
          aria-label="作业"
        >
          <div className="nav-icon-wrapper">
            <ScrollText size={22} />
            {dailyCount > 0 && <span className="nav-badge">{dailyCount}</span>}
          </div>
          <span>作业</span>
        </button>

        {/* Hidden — vocab tab not ready
        <button
          ref={mapRef}
          className={`nav-btn ${activeTab === 'map' ? 'active' : ''}`}
          onClick={() => handleNavClick('map')}
          aria-label="词汇"
        >
          <Grid size={22} />
          <span>词汇</span>
        </button>
        */}

        <button
          className={`nav-btn ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => handleNavClick('settings')}
          aria-label="设置"
        >
          <Settings size={22} />
          <span>设置</span>
        </button>

      </nav>
    </div>
  );
});

export default MobileStudentNav;
